/**
 * OLT inventory + ONU table + optical-sample fixtures.
 *
 * These stand in for the *proxy* surface: `GET /ops/v1/olts[/{id}/onus]` and
 * `GET /ops/v1/devices/{id}/optical` read the standalone OLT adapter
 * (`olt_adapter/`, drivers `canned_vsol` / `canned_cdata`), so the row keys here
 * are the adapter's own, verbatim (`olt_adapter/drivers/base.py`):
 *
 *   ONU row   `{sn, olt_id, pon_port, status, auth_state, profile,
 *              line_profile, service_profile, vlan, tr069}`
 *             with `status` `registered|unregistered` and `auth_state`
 *             `authorized|unauthorized`. The façade passes this object through
 *             untouched (`OnuTableResponse.items: list[dict]`), so the console
 *             treats `sn` as the only required key.
 *   optical   `{rx_dbm, tx_dbm, measured_at}` — ONE instantaneous sample, which
 *             is why the UI never draws a trend from it.
 *
 * Two deliberate states, because both are real answers of the API:
 *  - `olt-isfahan-02` is reachable with an **empty** ONU table.
 *  - `olt-mashhad-01` carries a `note` and count 0: `routers/olt_ops.py` degrades
 *    a single unreadable table into `onu_count: 0` plus
 *    `note: "onu table unavailable: <adapter code>"` instead of blanking the
 *    inventory — an unreadable OLT must never look like an empty one.
 *
 * The device → OLT association mirrors `_association()` (device doc `olt_id`,
 * else the newest activation job): a fixture device **without a subscriber
 * binding** has nothing on record, so its optical read is the documented
 * **409 `stale-snapshot`** ("no OLT association known"), not an error to hide.
 */

import {
  DEGRADING_HORIZON_DAYS,
  DEGRADING_MIN_SAMPLES_PER_HALF,
  OPTICAL_BUCKET_SECONDS,
  OPTICAL_WINDOW_SECONDS,
  RX_BIN_DBM,
  WORST_LIMIT_DEFAULT,
  type OltInventoryItem,
  type OnuTableRow,
  type OpticalDegrading,
  type OpticalDegradingDevice,
  type OpticalDegradingWindow,
  type OpticalHistory,
  type OpticalHistoryPoint,
  type OpticalRead,
  type OpticalWindow,
  type OpticalWorst,
  type OpticalWorstDevice,
  type PonCapacityResponse,
  type PonCapacityRow,
} from '../api/types';
import {
  CANNED_CWMP_URL,
  CANNED_INFORM_INTERVAL,
  DEFAULT_LINE_PROFILE,
  DEFAULT_SERVICE_PROFILE,
  FIXTURE_OLT_IDS,
  FIXTURE_PON_PORTS,
} from './activations';
import { FIXTURE_OPTICAL_THRESHOLDS, FIXTURE_PON_CAPACITY } from './identity';
import { FIXTURE_DEVICES, isoAgo, type FixtureDevice } from './devices';

/** The brands the adapter actually drives today (`olt_adapter/olts.json`). */
export interface FixtureOltSpec {
  olt_id: string;
  brand: string;
  host: string;
  enabled: boolean;
}

export const FIXTURE_OLT_INVENTORY: readonly FixtureOltSpec[] = [
  { olt_id: 'olt-tehran-01', brand: 'vsol', host: '10.0.10.11', enabled: true },
  { olt_id: 'olt-tehran-02', brand: 'cdata', host: '10.0.10.12', enabled: true },
  { olt_id: 'olt-karaj-01', brand: 'vsol', host: '10.0.10.21', enabled: true },
  { olt_id: 'olt-qom-01', brand: 'cdata', host: '10.0.10.31', enabled: true },
  { olt_id: 'olt-isfahan-02', brand: 'vsol', host: '10.0.10.42', enabled: false },
  { olt_id: 'olt-mashhad-01', brand: 'cdata', host: '10.0.10.51', enabled: true },
];

/**
 * OLTs whose ONU table the fixture pretends the adapter could not read — exactly
 * the degrade branch of `list_olts()`. The code after the colon is the adapter's
 * own (`adapter-unreachable`), not a console label.
 */
export const FIXTURE_OLT_TABLE_NOTES: Readonly<Record<string, string>> = {
  'olt-mashhad-01': 'onu table unavailable: adapter-unreachable',
};

/** `onu_count` of the readable tables is this many devices per OLT at most. */
const OLT_CYCLE = 4;

export interface OltAssociation {
  olt_id: string;
  pon_port: string;
}

/**
 * The fixture's association rule (mirrors `_association()`): bound devices sit on
 * an OLT and a PON port, unbound ones have nothing on record. `index` is the
 * device's position in `FIXTURE_DEVICES`, so the placement is deterministic.
 */
export function associationOf(device: FixtureDevice, index: number): OltAssociation | null {
  if (device.nas_ref === null) return null;
  const olt_id = FIXTURE_OLT_IDS[index % OLT_CYCLE];
  const pon_port = FIXTURE_PON_PORTS[Math.floor(index / OLT_CYCLE) % FIXTURE_PON_PORTS.length];
  if (!olt_id || !pon_port) return null;
  return { olt_id, pon_port };
}

function onuRowOf(device: FixtureDevice, index: number): OnuTableRow | null {
  const link = associationOf(device, index);
  if (!link) return null;
  const informed = device.last_inform_at !== null;
  return {
    sn: device.serial,
    olt_id: link.olt_id,
    pon_port: link.pon_port,
    // The canned drivers flip `status` with registration, `auth_state` with the
    // authorize/deauthorize calls — an offline-but-authorized ONU reads
    // `unregistered` + `authorized`, which is the interesting combination.
    status: device.online ? 'registered' : 'unregistered',
    auth_state: 'authorized',
    profile: null,
    line_profile: index % 3 === 0 ? 'LP-GPON-25' : DEFAULT_LINE_PROFILE,
    service_profile: index % 2 === 0 ? DEFAULT_SERVICE_PROFILE : 'SP-INET-300M',
    vlan: index % 4 === 0 ? null : 100 + (index % 40),
    tr069: informed
      ? {
          acs_url: CANNED_CWMP_URL,
          inform_interval: CANNED_INFORM_INTERVAL,
          pushed_at: isoAgo(index + 5),
        }
      : null,
  };
}

/** olt_id → ONU table, in the adapter's own order (`sn` ascending). */
export function buildOltTables(): Map<string, OnuTableRow[]> {
  const tables = new Map<string, OnuTableRow[]>();
  for (const spec of FIXTURE_OLT_INVENTORY) {
    tables.set(spec.olt_id, []);
  }
  FIXTURE_DEVICES.forEach((device, index) => {
    const row = onuRowOf(device, index);
    if (!row) return;
    const bucket = tables.get(row.olt_id ?? '');
    if (bucket) bucket.push(row);
  });
  for (const [oltId, rows] of tables) {
    rows.sort((a, b) => a.sn.localeCompare(b.sn));
    tables.set(oltId, rows);
  }
  return tables;
}

/** One inventory row, with the count/note rule of `list_olts()`. */
export function inventoryRowOf(
  spec: FixtureOltSpec,
  table: readonly OnuTableRow[] | undefined,
): OltInventoryItem {
  const note = FIXTURE_OLT_TABLE_NOTES[spec.olt_id] ?? null;
  return {
    olt_id: spec.olt_id,
    brand: spec.brand,
    host: spec.host,
    enabled: spec.enabled,
    // A table that could not be read is reported as 0 + the note, never as empty.
    onu_count: note === null ? (table?.length ?? 0) : 0,
    note,
  };
}

/**
 * The single optical sample of one serial. `rx_dbm`/`tx_dbm` stay null for a
 * device the fixture has no normalised reading for — the adapter answers null
 * when the SNMP read comes back empty, which is a reading, not a missing field.
 */
export function opticalOf(
  device: FixtureDevice,
  association: OltAssociation,
  index: number,
): OpticalRead {
  return {
    rx_dbm: device.rxDbm,
    tx_dbm: device.txDbm,
    measured_at: isoAgo((index % 24) + 1),
    olt_id: association.olt_id,
    sn: device.serial,
  };
}

/**
 * The stored series of one device over one window (V3), mirroring what the façade keeps:
 * one row per sweep tick, so the fixture has ONE reading per bucket of the window it was
 * asked for, all of them the device's own normalised RX/TX.
 *
 * Two rules are the point of this function, and both are the server's:
 *  - a device with no reading (`rxDbm === null`) has no STORED rows either, so the answer
 *    is an empty series — not a line at zero, and not a failure;
 *  - the association is NOT needed. Readings already taken belong to the device; `olt_id`
 *    is the OLT of the newest row (the association while it stands, null when there is no
 *    row), never a reason to refuse the past.
 */
export function opticalHistoryOf(
  device: FixtureDevice,
  association: OltAssociation | null,
  window: OpticalWindow,
  nowMs: number = Date.now(),
): OpticalHistory {
  const bucket = OPTICAL_BUCKET_SECONDS[window];
  const startMs = nowMs - OPTICAL_WINDOW_SECONDS[window] * 1000;
  const empty = {
    acs_device_id: device.acs_device_id,
    olt_id: null,
    window,
    window_start: new Date(startMs).toISOString(),
    window_end: new Date(nowMs).toISOString(),
    bucket_seconds: bucket,
    points: [] as OpticalHistoryPoint[],
    samples: 0,
    last_sampled_at: null,
    note: 'no reading was stored for this device in this window',
  };
  if (device.rxDbm === null) return empty;

  const points: OpticalHistoryPoint[] = [];
  for (let at = startMs; at < nowMs; at += bucket * 1000) {
    points.push({
      at: new Date(at).toISOString(),
      rx_dbm: device.rxDbm,
      tx_dbm: device.txDbm,
      samples: 1,
    });
  }
  return {
    ...empty,
    olt_id: association?.olt_id ?? null,
    points,
    samples: points.length,
    last_sampled_at: points[points.length - 1]?.at ?? null,
    note: 'one stored reading per bucket, from the fixture sweep',
  };
}

/**
 * `GET /ops/v1/optical/worst` (V3b, G-TLM-03) — the fleet's darkest ONUs, rolled up from the
 * fixture's own history.
 *
 * It is built BY REDUCING `opticalHistoryOf` for every fixture device, not from a second list of
 * numbers (plan ruling D7): the fleet answer must be the same fact as the per-device answer, or the
 * console would show two different stories about the same ONU and the fixture would stop being a
 * mirror of the server. A device whose series is empty is ABSENT — the server stores no row for an
 * unmeasured sample, so it cannot appear in a roll-up either, and `devices` counts devices, never
 * readings.
 */
export function opticalWorstOf(
  window: OpticalWindow,
  limit: number = WORST_LIMIT_DEFAULT,
  nowMs: number = Date.now(),
): OpticalWorst {
  const rollup: OpticalWorstDevice[] = [];
  let samples = 0;

  FIXTURE_DEVICES.forEach((device, index) => {
    const series = opticalHistoryOf(device, associationOf(device, index), window, nowMs);
    if (series.points.length === 0) return;
    const values = series.points.map((point) => point.rx_dbm);
    rollup.push({
      acs_device_id: device.acs_device_id,
      rx_min_dbm: Math.min(...values),
      rx_avg_dbm: values.reduce((total, value) => total + value, 0) / values.length,
      samples: values.length,
      last_sampled_at: series.last_sampled_at,
    });
    samples += values.length;
  });

  const ordered = [...rollup].sort(
    (a, b) => a.rx_min_dbm - b.rx_min_dbm || a.acs_device_id.localeCompare(b.acs_device_id),
  );
  const bins = new Map<number, number>();
  rollup.forEach((row) => {
    const edge = Math.floor(row.rx_min_dbm / RX_BIN_DBM) * RX_BIN_DBM;
    bins.set(edge, (bins.get(edge) ?? 0) + 1);
  });

  return {
    window,
    window_start: new Date(nowMs - OPTICAL_WINDOW_SECONDS[window] * 1000).toISOString(),
    window_end: new Date(nowMs).toISOString(),
    limit,
    devices: rollup.length,
    samples,
    bin_dbm: RX_BIN_DBM,
    worst: ordered.slice(0, Math.max(0, Math.min(limit, ordered.length))),
    histogram: [...bins.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([bin_dbm, devices]) => ({ bin_dbm, devices })),
    note: 'ranked by the darkest reading the fixture sweep stored; devices, not rows',
  };
}

/** Half of a published window in seconds — the same partition the door computes (D1). */
export function opticalHalfSeconds(window: OpticalDegradingWindow): number {
  return Math.floor(OPTICAL_WINDOW_SECONDS[window] / 2);
}

/**
 * `GET /ops/v1/olts/{olt_id}/pon-capacity` (V7, G-PRC-08) — the fixture's capacity mirror.
 *
 * It reduces the fixture's OWN ONU tables (`buildOltTables()`) the exact way
 * `acs_service/pon_capacity.py::port_rows` reduces the adapter's table: count per port,
 * one-decimal utilization, inclusive `at_capacity`, worst first with the port name as
 * tie-break, and population counts of the WHOLE table. The capacity is the DECLARED one
 * from the same stored settings document the settings door shows — reading the default
 * literal here would let the fixture keep measuring against 64 after the operator
 * declared 8, and the panel would disagree with the server about which port is full.
 *
 * An OLT whose table the fixture cannot read is NOT answered with `ports: null` (the
 * fixture has no «nobody read» adapter state to answer null FROM) — it follows the
 * convention `listOltOnus` already has and lets the caller see `adapter-unreachable`.
 */
export function ponCapacityOf(
  oltId: string,
  stored: Record<string, number> = {},
): PonCapacityResponse {
  const capacity = stored.pon_capacity_onus_per_port ?? FIXTURE_PON_CAPACITY.default;
  const counts = new Map<string, number>();
  for (const rows of buildOltTables().values()) {
    for (const row of rows) {
      if (row.olt_id !== oltId) continue;
      const port = row.pon_port ?? '';
      if (!port) continue;
      counts.set(port, (counts.get(port) ?? 0) + 1);
    }
  }
  const rows: PonCapacityRow[] = [...counts.entries()]
    .map(([pon_port, onu_count]) => ({
      pon_port,
      onu_count,
      capacity,
      utilization_pct: Math.round((onu_count / capacity) * 1000) / 10,
      remaining: capacity - onu_count,
      at_capacity: onu_count >= capacity,
    }))
    .sort((a, b) => b.utilization_pct - a.utilization_pct || a.pon_port.localeCompare(b.pon_port));
  return {
    olt_id: oltId,
    declared_capacity: capacity,
    capacity_source: 'settings:pon_capacity_onus_per_port',
    ports: rows,
    ports_counted: rows.length,
    total_onus: rows.reduce((sum, row) => sum + row.onu_count, 0),
    ports_at_capacity: rows.filter((row) => row.at_capacity).length,
    unknown_fields: [],
    note:
      'pon-capacity: the occupancy is what the fixture ONU table mentions; the ' +
      'capacity is DECLARED (settings), never read from a device',
  };
}

/**
 * `GET /ops/v1/optical/degrading` (V3c, G-TLM-03) — the fleet's decline rates, reduced from the
 * fixture's OWN history series exactly the way the door reduces stored rows: one mean per half,
 * partitioned by time, with the same guard and the same horizon.
 *
 * D7 is the ruling this function exists to obey. The fixture's series is FLAT — `opticalHistoryOf`
 * emits one reading per bucket and all of them are that device's own normalised RX — so its two
 * halves have the same mean and the honest answer for this fleet is `declining: []` with the fleet
 * counted in `devices`. Fabricating a falling ONU so the panel has something to draw would be the
 * exact finding this repository calls «a number that looks trustworthy»: the console would show a
 * decline the mirror says is impossible, and a test could no longer tell the two providers apart.
 *
 * The thresholds come from the same stored document the settings door shows, because this is the one
 * optical door that answers about the future (D3) — reading a literal here would let the fixture and
 * the server disagree about which line a countdown aims at.
 */
export function opticalDegradingOf(
  window: OpticalDegradingWindow,
  limit: number = WORST_LIMIT_DEFAULT,
  nowMs: number = Date.now(),
  stored: Record<string, number> = {},
): OpticalDegrading {
  const startMs = nowMs - OPTICAL_WINDOW_SECONDS[window] * 1000;
  const halfSeconds = opticalHalfSeconds(window);
  const middleMs = startMs + halfSeconds * 1000;
  const warn = stored.optical_warn_rx_dbm ?? FIXTURE_OPTICAL_THRESHOLDS.warn;
  const critical = stored.optical_critical_rx_dbm ?? FIXTURE_OPTICAL_THRESHOLDS.critical;

  const rows: Array<{
    acs_device_id: string;
    rx_avg_first: number;
    rx_avg_second: number;
    rx_min_second: number | null;
    samples_first: number;
    samples_second: number;
    last_sampled_at: string | null;
    enough: boolean;
    rate_dbm_per_day: number | null;
  }> = [];
  let samples = 0;

  FIXTURE_DEVICES.forEach((device, index) => {
    const series = opticalHistoryOf(device, associationOf(device, index), window, nowMs);
    if (series.points.length === 0) return;
    const older = series.points.filter((point) => Date.parse(point.at) <= middleMs);
    const newer = series.points.filter((point) => Date.parse(point.at) > middleMs);
    const mean = (points: OpticalHistoryPoint[]) =>
      points.length === 0
        ? null
        : points.reduce((total, point) => total + point.rx_dbm, 0) / points.length;
    const firstAvg = mean(older);
    const secondAvg = mean(newer);
    const enough =
      firstAvg !== null &&
      secondAvg !== null &&
      older.length >= DEGRADING_MIN_SAMPLES_PER_HALF &&
      newer.length >= DEGRADING_MIN_SAMPLES_PER_HALF;
    rows.push({
      acs_device_id: device.acs_device_id,
      rx_avg_first: firstAvg ?? 0,
      rx_avg_second: secondAvg ?? 0,
      rx_min_second: newer.length === 0 ? null : Math.min(...newer.map((p) => p.rx_dbm)),
      samples_first: older.length,
      samples_second: newer.length,
      last_sampled_at: newer[newer.length - 1]?.at ?? null,
      enough,
      rate_dbm_per_day: enough
        ? Math.round(
            (((secondAvg as number) - (firstAvg as number)) / (halfSeconds / 86400)) * 1000,
          ) / 1000
        : null,
    });
    samples += older.length + newer.length;
  });

  const falling = rows
    .filter((row) => row.enough && row.rate_dbm_per_day !== null && row.rate_dbm_per_day < 0)
    .sort(
      (a, b) =>
        (a.rate_dbm_per_day as number) - (b.rate_dbm_per_day as number) ||
        a.acs_device_id.localeCompare(b.acs_device_id),
    );

  const declining: OpticalDegradingDevice[] = falling
    .slice(0, Math.max(0, Math.min(limit, falling.length)))
    .map((row) => {
      const already = row.rx_avg_second <= warn;
      const days = already ? null : (row.rx_avg_second - warn) / -(row.rate_dbm_per_day as number);
      const within = days !== null && days <= DEGRADING_HORIZON_DAYS;
      return {
        acs_device_id: row.acs_device_id,
        rx_avg_first: Math.round(row.rx_avg_first * 10) / 10,
        rx_avg_second: Math.round(row.rx_avg_second * 10) / 10,
        rx_min_second: row.rx_min_second === null ? null : Math.round(row.rx_min_second * 10) / 10,
        samples_first: row.samples_first,
        samples_second: row.samples_second,
        rate_dbm_per_day: row.rate_dbm_per_day as number,
        days_to_warn: within ? Math.round((days as number) * 10) / 10 : null,
        within_horizon: within,
        already_below_warn: already,
        last_sampled_at: row.last_sampled_at,
      };
    });

  return {
    window,
    window_start: new Date(startMs).toISOString(),
    window_end: new Date(nowMs).toISOString(),
    half_seconds: halfSeconds,
    limit,
    devices: rows.length,
    samples,
    insufficient: rows.filter((row) => !row.enough).length,
    warn_rx_dbm: warn,
    critical_rx_dbm: critical,
    horizon_days: DEGRADING_HORIZON_DAYS,
    min_samples_per_half: DEGRADING_MIN_SAMPLES_PER_HALF,
    declining,
    note: 'half-over-half from the fixture’s own stored series; the fleet is flat, so nothing falls',
  };
}

/** serial → (device, index), so an optical read by `sn` is O(1) and deterministic. */
export function deviceBySerial(serial: string): { device: FixtureDevice; index: number } | null {
  const index = FIXTURE_DEVICES.findIndex((d) => d.serial === serial);
  if (index < 0) return null;
  const device = FIXTURE_DEVICES[index];
  return device ? { device, index } : null;
}

export const UNKNOWN_OLT_SAMPLE = 'olt-unknown-99';
